import { PrestationPricingMode, PrestationType } from '@cpce/database/enums';
import z from 'zod';
import { zDateField } from '../utils/date.utils.js';
import { isPrestationTypeConsistent } from '../utils/prestations.utils.js';
import { zodQueryBoolean } from './boolean.types.js';
import { faceValueCriteriaSchema } from './face-value-criteria.types.js';
import { dossierAnswersSchema, prestationQuestionDataSchema, } from './prestation-questions.types.js';
export const prestationWithRelations = {
    include: {
        supplier: { select: { id: true, name: true } },
        accountingPeriod: { select: { id: true, name: true } },
        accountingCategories: { select: { id: true, name: true, parentId: true } },
        prices: { orderBy: { id: 'asc' } },
        questions: { where: { archivedAt: null }, orderBy: { position: 'asc' } },
        productFinancialAccount: { select: { id: true, code: true } },
        chargeFinancialAccount: { select: { id: true, code: true } },
        disbursementAccount: { select: { id: true, code: true, name: true } },
        _count: { select: { prestationParticipations: { where: { cancelledAt: null } } } },
    },
};
export const prestationPriceDataSchema = z.object({
    name: z.string().min(1, 'Le nom du tarif est requis'),
    price: z.coerce.number().nonnegative('Le prix doit être positif'),
    attributionCriteria: faceValueCriteriaSchema.nullable().optional(),
});
export const PRESTATION_CATEGORY_REQUIRED_ERROR = "La règle de subvention est requise : seule une adhésion peut s'en passer";
export const PRESTATION_EMPLOYEES_ONLY_ERROR = 'Cette prestation est réservée aux salariés : un ayant-droit ne peut pas y être inscrit';
export const PRESTATION_ADHESION_WITH_SUPPLIER_ERROR = 'Une adhésion est fournie par le CSE : elle ne peut pas avoir de fournisseur';
export const PRESTATION_ADHESION_TYPED_AMOUNT_ERROR = "Une adhésion se règle à un tarif prédéfini : pas de montant saisi à l'inscription";
export const PRESTATION_PRICING_MODE_FROZEN_ERROR = 'Impossible de changer le mode de tarification : la prestation a déjà des inscrits';
export const PRESTATION_TYPE_MISMATCH_ERROR = 'Le type de prestation ne correspond pas à son fournisseur ou à son mode de tarification';
const prestationDataBaseSchema = z.object({
    name: z.string().min(1, 'Le nom est requis'),
    maxParticipants: z.coerce.number().int().positive('Le nombre max doit être positif'),
    /** Null on an adhésion left unsubsidised, or when a product rule replaces the category. */
    ticketingCategoryId: z.number().int().positive(PRESTATION_CATEGORY_REQUIRED_ERROR).nullable(),
    /** A billetterie product rule's variant, which then replaces the category. */
    ticketingProductVariantCode: z.string().min(1).nullable().optional(),
    blocksBeyondSubsidyQuantity: z.boolean().optional(),
    /** Null: « Aucun », the CSE provides the prestation itself (see `Prestation.supplierId`). */
    supplierId: z.number().int().positive('Le fournisseur est requis').nullable(),
    isAdhesion: z.boolean().optional(),
    employeesOnly: z.boolean().optional(),
    pricingMode: z.enum(PrestationPricingMode).optional(),
    /** Inferred from the supplier, adhésion and pricing mode when left out. */
    type: z.enum(PrestationType).optional(),
    disbursementAccountId: z.number().int().positive().nullable().optional(),
    accountingPeriodId: z.number().int().positive("L'exercice comptable est requis"),
    validityStartDate: z.coerce.date(),
    registrationDeadline: z.coerce.date(),
    prices: z.array(prestationPriceDataSchema).optional(),
    questions: z.array(prestationQuestionDataSchema).optional(),
    accountingCategoryIds: z
        .array(z.number().int())
        .max(1, 'Une seule catégorie comptable est autorisée')
        .optional(),
});
export const PRESTATION_DISBURSEMENT_ACCOUNT_REQUIRED_ERROR = 'Le compte bancaire de décaissement est requis pour une prestation sans fournisseur';
// The subsidy comes from exactly one of a category and a product rule. Only an adhésion may go
// without either: its subsidy is optional.
export const hasOneSubsidyTargetUnlessAdhesion = (data) => !!data.isAdhesion || (data.ticketingCategoryId !== null) !== !!data.ticketingProductVariantCode;
// A créance is paid out by the CSE: it needs the account the money leaves from.
export const hasDisbursementAccountIfCreance = (data) => data.supplierId !== null || !!data.isAdhesion || !!data.disbursementAccountId;
export const prestationDataSchema = prestationDataBaseSchema
    .refine((data) => data.validityStartDate <= data.registrationDeadline, {
    message: "La date de validité ne peut pas être postérieure à la date limite d'inscription",
    path: ['registrationDeadline'],
})
    .refine((data) => !data.isAdhesion || data.supplierId === null, {
    message: PRESTATION_ADHESION_WITH_SUPPLIER_ERROR,
    path: ['supplierId'],
})
    .refine((data) => !data.isAdhesion || data.pricingMode !== PrestationPricingMode.typedAmount, {
    message: PRESTATION_ADHESION_TYPED_AMOUNT_ERROR,
    path: ['pricingMode'],
})
    .refine((data) => data.type === undefined ||
    isPrestationTypeConsistent({
        type: data.type,
        supplierId: data.supplierId,
        isAdhesion: !!data.isAdhesion,
        pricingMode: data.pricingMode ?? PrestationPricingMode.predefinedPrices,
    }), { message: PRESTATION_TYPE_MISMATCH_ERROR, path: ['type'] })
    .refine(hasOneSubsidyTargetUnlessAdhesion, {
    message: PRESTATION_CATEGORY_REQUIRED_ERROR,
    path: ['ticketingCategoryId'],
})
    .refine(hasDisbursementAccountIfCreance, {
    message: PRESTATION_DISBURSEMENT_ACCOUNT_REQUIRED_ERROR,
    path: ['disbursementAccountId'],
});
// Set on the Tarifs tab rather than in the prestation's form, which is why it is not in its data.
export const createPrestationSchema = prestationDataSchema.safeExtend({
    requiresJustificatif: z.boolean().optional(),
});
export const partialPrestationDataSchema = prestationDataBaseSchema
    .omit({ prices: true, questions: true })
    .extend({ requiresJustificatif: z.boolean() })
    .partial()
    .refine((data) => data.validityStartDate === undefined ||
    data.registrationDeadline === undefined ||
    data.validityStartDate <= data.registrationDeadline, {
    message: "La date de validité ne peut pas être postérieure à la date limite d'inscription",
    path: ['registrationDeadline'],
})
    .refine((data) => (data.ticketingCategoryId === undefined && data.ticketingProductVariantCode === undefined) ||
    hasOneSubsidyTargetUnlessAdhesion({
        ...data,
        ticketingCategoryId: data.ticketingCategoryId ?? null,
    }), { message: PRESTATION_CATEGORY_REQUIRED_ERROR, path: ['ticketingCategoryId'] });
export const updatePrestationPriceSchema = prestationPriceDataSchema.partial();
export const setPrestationArchivedSchema = z.object({ archived: z.boolean() });
export const settleCreancesSchema = z.object({
    participationIds: z.array(z.number().int().positive()).min(1, 'Au moins un dossier est requis'),
    date: zDateField('La date du règlement est requise'),
    instrument: z.enum(['bankTransfer', 'check', 'bankCard']),
    instrumentNumber: z.string().trim().optional(),
});
export const PRESTATION_PRICE_IN_USE_ERROR = 'Ce tarif ne peut pas être supprimé car il est utilisé par une commande, une facture ou un dossier';
export const PRESTATION_PRICE_AMOUNT_LOCKED_ERROR = 'Ce tarif est utilisé par un dossier : son montant ne peut plus être modifié. Créez un nouveau tarif.';
/** What a participant's amount is computed from (see `computeParticipantAmount`). */
export const participantAmountSelect = {
    select: { quantity: true, typedAmount: true, prestationPrice: { select: { price: true } } },
};
/** What `calculatePrestationDebt` reads. */
export const participationDebtSelect = {
    participants: participantAmountSelect,
    cancelledAt: true,
    prestationTransactions: {
        select: { amount: true, rejectedAmount: true, transaction: { select: { cancelledAt: true } } },
    },
    saleTransaction: {
        select: {
            transactionAccountMovements: {
                select: {
                    transactionType: true,
                    amount: true,
                    financialAccount: { select: { code: true } },
                },
            },
        },
    },
};
export const prestationParticipationWithRelations = {
    include: {
        prestation: {
            include: {
                disbursementAccount: { select: { code: true, name: true, producesTransferFile: true } },
            },
        },
        participants: { include: { prestationPrice: true } },
        prestationTransactions: {
            include: {
                transaction: true,
            },
        },
        saleTransaction: {
            include: {
                transactionAccountMovements: { include: { financialAccount: true } },
                journal: true,
            },
        },
        cancellationTransaction: { select: { date: true } },
        debtReminders: {
            include: {
                communication: true,
            },
            orderBy: {
                communication: { dateTime: 'desc' },
            },
        },
        logs: {
            include: { user: true },
            orderBy: { dateTime: 'desc' },
        },
        _count: { select: { justificatifs: true } },
    },
};
export const participationJustificatifWithFile = {
    include: { file: true, uploadedBy: { select: { name: true } } },
};
export const prestationParticipationsFilters = z.object({
    employeeIds: z.coerce.number().array().optional(),
    beneficiaryIds: z.coerce.number().array().optional(),
    includeCancelled: zodQueryBoolean().optional(),
});
export const prestationsFilters = z.object({
    supplierId: z.coerce.number().optional(),
    accountingCategoryId: z.coerce.number().optional(),
    availableOnly: zodQueryBoolean().optional(),
    includeArchived: zodQueryBoolean().optional(),
});
export const subsidisingRuleQuerySchema = z
    .object({
    ticketingCategoryId: z.coerce.number().int().positive().optional(),
    ticketingProductVariantCode: z.string().min(1).optional(),
})
    .refine((query) => !query.ticketingCategoryId !== !query.ticketingProductVariantCode, {
    message: 'Une catégorie ou un produit est requis',
});
/**
 * One entry per person taking part, each with their own tarif. The server groups them by
 * paying salarié (`externalEmployeeId`) into one dossier per payer — the user never picks
 * that grouping, it follows from who each ayant-droit is attached to.
 */
export const participationParticipantSchema = z.object({
    externalEmployeeId: z.number().int().optional(),
    externalBeneficiaryId: z.number().int().optional(),
    /**
     * 0 while the wizard is still on the selection step; the Tarif step fills it in. Left out on a
     * prestation whose amount is typed at registration.
     */
    prestationPriceId: z.number().int().nonnegative().optional(),
    /** The line's amount on such a prestation: an invoice total, whatever its quantity. */
    typedAmount: z.number().positive('Le montant doit être positif').optional(),
    /** Places taken by this participant at the chosen tarif (e.g. 6 tickets restaurant for one person). */
    quantity: z.number().int().min(1, 'La quantité doit être au moins 1').default(1),
});
export const createPrestationParticipationSchema = z.object({
    prestationId: z.number().int().positive('La prestation est requise'),
    // Normalized to midnight: it dates the sale écriture, and one carrying a time of day falls
    // outside an exercice whose `endDate` is a plain calendar day.
    date: zDateField('La date du dossier est requise'),
    participants: z
        .array(participationParticipantSchema)
        .min(1, 'Au moins un bénéficiaire est requis'),
    dossierAnswers: z.array(dossierAnswersSchema).default([]),
});
export const estimateParticipationSubsidiesSchema = createPrestationParticipationSchema.pick({
    prestationId: true,
    participants: true,
});
/** The body of a refused creation that names its failing lines: nothing was created. */
export const participationCreationRefusalSchema = z.object({
    message: z.string(),
    failures: z.array(z.object({ participantIndex: z.number().int(), error: z.string() })),
});
export const updatePrestationParticipationSchema = z.object({
    date: z.coerce.date().optional(),
    remarks: z.string().nullable().optional(),
});
// Tarif, typed amount and quantity go through the same path: same guards (cancelled dossier,
// règlement already recorded, subsidy consumed) and the same subsidy re-estimation behind them.
export const updateParticipantSchema = z
    .object({
    prestationPriceId: z.number().int().positive('Le tarif est requis').optional(),
    typedAmount: z.number().positive('Le montant doit être positif').optional(),
    quantity: z.number().int().min(1, 'La quantité doit être au moins 1').optional(),
})
    .refine((data) => data.prestationPriceId !== undefined ||
    data.typedAmount !== undefined ||
    data.quantity !== undefined, { message: 'Aucune modification demandée' });
export const sendParticipantsEmailSchema = z.object({
    participationIds: z.array(z.number().int().positive()).min(1, 'Au moins un dossier est requis'),
    subject: z.string().min(1, "L'objet est requis"),
    textContent: z.string().min(1, 'Le contenu est requis'),
    attachment: z
        .object({
        content: z.string(),
        name: z.string(),
        type: z.string(),
    })
        .optional(),
});
